import { createMemo, For, JSX, onCleanup, onMount, Show } from 'solid-js';
import { Dynamic, Portal } from 'solid-js/web';
import { useDropdown } from '@trimble/solidjs-utils';
import { useFloatingMenuPosition } from '../control-dropdown/useFloatingMenuPosition';
import type {
  StrokeOptionDropdownOption,
  StrokeOptionDropdownOptionGroup,
  StrokeOptionDropdownProps,
} from './StrokeOptionDropdown.types';

export type {
  StrokeOptionDropdownOption,
  StrokeOptionDropdownOptionGroup,
  StrokeOptionDropdownProps,
} from './StrokeOptionDropdown.types';

/** Flattens groups into a single list of options (each group's parent if present, then its children) for lookup. */
function getAllOptions<T>(
  groups: StrokeOptionDropdownOptionGroup<T>[]
): StrokeOptionDropdownOption<T>[] {
  const out: StrokeOptionDropdownOption<T>[] = [];
  for (const group of groups) {
    if (group.parent) out.push(group.parent);
    out.push(...group.children);
  }
  return out;
}

interface OptionButtonProps<T> {
  option: StrokeOptionDropdownOption<T>;
  selectedValue: T;
  disabled: boolean | undefined;
  onSelect(value: T): void;
}

function OptionButton<T>(props: OptionButtonProps<T>): JSX.Element {
  const isSelected = (): boolean => props.option.value === props.selectedValue;
  const isSelectable = (): boolean =>
    props.option.selectable !== false && !props.disabled;
  return (
    <button
      type="button"
      role="option"
      aria-selected={isSelected()}
      aria-disabled={!isSelectable()}
      data-testid={props.option.testId}
      class={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm ${
        isSelected()
          ? 'bg-blue-pale text-blue'
          : 'text-[var(--modus-wc-color-base-content,#171C1E)] hover:bg-gray-0'
      } ${
        props.disabled
          ? 'cursor-not-allowed opacity-50'
          : isSelectable()
            ? 'cursor-pointer'
            : 'cursor-default'
      }`}
      onClick={() => isSelectable() && props.onSelect(props.option.value)}
      disabled={props.disabled}
    >
      <span class="whitespace-nowrap">{props.option.label}</span>
      {props.option.renderPreview ? (
        <span class="flex h-5 min-h-5 w-10 shrink-0 items-center" aria-hidden>
          <Dynamic component={props.option.renderPreview} />
        </span>
      ) : (
        <span class="flex h-5 min-h-5 w-10 shrink-0 items-center" aria-hidden />
      )}
    </button>
  );
}

export function StrokeOptionDropdown<T>(
  props: StrokeOptionDropdownProps<T>
): JSX.Element {
  const { isOpen, toggle, close, ref, dropdownId } = useDropdown();
  let anchorRef: HTMLElement | undefined;
  const { menuStyle, setMenuSectionRef } = useFloatingMenuPosition({
    getAnchor: () => anchorRef,
  });

  const allOptions = createMemo(() => getAllOptions(props.options));

  const selectedOption = createMemo(() =>
    allOptions().find((opt) => opt.value === props.value)
  );

  const selectedLabel = createMemo(() => selectedOption()?.label ?? '');

  const handleSelect = (value: T): void => {
    props.onValueChange(value);
    close();
  };

  const handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      close();
    }
  };

  onMount(() => {
    document.addEventListener('keydown', handleKeyDown);
  });

  onCleanup(() => {
    document.removeEventListener('keydown', handleKeyDown);
  });

  return (
    <div ref={ref} class="relative" data-testid={`${props.triggerTestId}-root`}>
      <modus-wc-tooltip
        content={props.disabledReason}
        position="bottom"
        disabled={!props.disabled}
      >
        <div
          ref={(el) => {
            anchorRef = el;
          }}
          class={`w-full min-h-[2.25rem] rounded-[2px] border bg-white overflow-hidden ${
            props.disabled
              ? 'cursor-not-allowed opacity-50 border-gray-2'
              : 'cursor-pointer border-gray-2 hover:border-gray-4'
          } ${isOpen() ? 'border-gray-5' : ''}`}
        >
          <button
            type="button"
            data-testid={props.triggerTestId}
            class="flex w-full min-h-[2.25rem] items-center justify-between gap-2 border-0 bg-transparent px-3 py-2 text-left text-sm text-[var(--modus-wc-color-base-content,#171C1E)] outline-none"
            onClick={() => !props.disabled && toggle()}
            disabled={props.disabled}
            aria-haspopup="listbox"
            aria-expanded={isOpen()}
            aria-label={props.ariaLabel}
          >
            <span class="truncate">{selectedLabel()}</span>
            <span class="flex shrink-0" aria-hidden>
              <i
                class={`icon-font ${isOpen() ? 'tc-icon-arrow-drop-up' : 'tc-icon-arrow-drop-down'}`}
                aria-hidden
              />
            </span>
          </button>
        </div>
      </modus-wc-tooltip>
      <Show when={isOpen()}>
        <Portal>
          <div
            ref={setMenuSectionRef}
            class="fixed z-dropdown min-w-[9rem] w-max rounded-[2px] border border-gray-2 bg-white py-1 shadow-soft pointer-events-auto"
            style={menuStyle()}
            role="listbox"
            data-testid={props.listTestId}
            data-dropdown-id={dropdownId}
          >
            <For each={props.options}>
              {(group, index) => (
                <>
                  {index() > 0 ? (
                    <div class="my-1 border-t border-gray-2" aria-hidden />
                  ) : null}
                  {group.parent ? (
                    <>
                      <OptionButton
                        option={group.parent}
                        selectedValue={props.value}
                        disabled={props.disabled}
                        onSelect={handleSelect}
                      />
                      {group.children.length > 0 ? (
                        <>
                          <div
                            class="my-1 border-t border-gray-2"
                            aria-hidden
                          />
                          <For each={group.children}>
                            {(option) => (
                              <OptionButton
                                option={option}
                                selectedValue={props.value}
                                disabled={props.disabled}
                                onSelect={handleSelect}
                              />
                            )}
                          </For>
                        </>
                      ) : null}
                    </>
                  ) : (
                    <For each={group.children}>
                      {(option) => (
                        <OptionButton
                          option={option}
                          selectedValue={props.value}
                          disabled={props.disabled}
                          onSelect={handleSelect}
                        />
                      )}
                    </For>
                  )}
                </>
              )}
            </For>
          </div>
        </Portal>
      </Show>
    </div>
  );
}
